/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useContext, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as yup from 'yup';
import { useFormik } from 'formik';
import Modal from 'react-bootstrap/Modal';
import 'bootstrap/dist/css/bootstrap.min.css';
import classNames from 'classnames';
import {
  getTimezoneDate,
  getTimezoneTime,
  setTimezoneDate,
  setTimezoneTime,
} from '../../helpers/timezones';
import UserService from '../../services/UserService';
import AdminService from '../../services/AdminService';
import { UiContext } from '../../context/UiContext';
// import { getTime } from '../../helpers/getEvents';
import { hasAuthData } from '../../helpers/authStorage';

import FormButton from '../../components/FormButton';
import TextInput from '../../components/Form/TextInput';
import Dropdown from '../../components/Form/Dropdown';
import TimeRange from '../../components/Form/TimeRange';
import SelectedUsers from '../../components/Dashboard/SelectedUsers';
import Textarea from '../../components/Form/Textarea';
import MultiDropdown from '../../components/Form/MultiDropdown';
import FormDropdown from '../../components/Form/FormDropdown';
import DatePicker from '../../components/Form/DatePicker';
import SegmentedControl from '../../components/SegmentedControl';

import { startTimeNotInPastIfToday } from '../../helpers/validation';
import classes from './styles.module.scss';

function convertToAmPm(timeString) {
  // Split the time string into hours and minutes
  const [hours, minutes] = timeString.split(':');

  // Convert hours part to number to perform calculations
  const hoursInt = parseInt(hours, 10);

  // Determine AM or PM based on the hours
  const amPm = hoursInt >= 12 ? 'PM' : 'AM';

  // Convert 24-hour format to 12-hour format by finding the modulus of hoursInt and 12
  // and handling the special case of 0 (which should be converted to 12)
  const twelveHourFormat = hoursInt % 12 || 12;

  // Concatenate the converted hours with minutes and AM/PM suffix
  return `${twelveHourFormat}:${minutes} ${amPm}`;
}

function getEventDate(date) {
  const tempDate = new Date(date);

  return `${tempDate.getDate()} ${tempDate.toLocaleString('en-us', {
    month: 'long',
  })} ${tempDate.getFullYear()}
  `;
}

export default function ScheduleInterviewModal({
  show,
  handleClose,
  candidate,
  refetch,
  initialContactType = 'Internal',
  editableInterview,
  isViewDetails,
}) {
  console.log('editableInterview', editableInterview);
  const [isTriedToSubmit, setIsTriedToSubmit] = useState(false);

  const { showNotification, setIsFetching, showModal } = useContext(UiContext);

  const { t } = useTranslation();

  const isAuthorized = hasAuthData();

  const isEdit = editableInterview && editableInterview?.id && !isViewDetails;

  const { data: job } = useQuery({
    queryKey: ['myJob', candidate?.jobPostId],
    queryFn: () => AdminService.getJobPost({ id: candidate?.jobPostId }),
    enabled: !!candidate?.jobPostId,
  });

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: UserService.getMe,
    enabled: isAuthorized,
  });

  const closeAndNotify = (message) => {
    handleClose();
    refetch();
    showNotification({
      text: message,
    });
  };

  const contactTypeOptions = useMemo(() => {
    const base = [
      {
        value: 'Internal',
        label: t('common.InterviewContactTypeOptions.Internal'),
      },
      {
        value: 'External',
        label: t('common.InterviewContactTypeOptions.External'),
      },
      {
        value: 'Client',
        label: t('common.InterviewContactTypeOptions.Client'),
      },
    ];

    if (initialContactType === 'Client') {
      return base.filter((o) => o.value === 'Client');
    }

    // Default: Internal (and External available)
    return base.filter((o) => o.value !== 'Client');
  }, [t, initialContactType]);

  // (moved below formik)

  const validationSchema = useMemo(() => {
    const requiredMsg = t('modals.ScheduleInterviewModal.requiredField');
    return yup.object({
      interviewDate: yup.date().required(requiredMsg),
      type: yup.object().required(requiredMsg),
      customLocation: yup
        .string()
        .nullable()
        .trim()
        .when('type', {
          is: (type) => type?.value === 'InPerson',
          then: (schema) => schema.required(requiredMsg),
          otherwise: (schema) => schema.notRequired(),
        }),
      connectionDetail: yup
        .string()
        .nullable()
        .transform((val) => (val ? val.trim() : val))
        .when('type', {
          is: (type) => type?.value !== 'InPerson',
          then: (schema) => schema.required(requiredMsg),
          otherwise: (schema) => schema.notRequired(),
        })
        .when('type', {
          is: (type) => type?.value === 'Audio',
          then: (schema) =>
            schema.matches(
              /^[+]?[\d\s\-().]+$/,
              t('modals.ScheduleInterviewModal.invalidPhoneNumber')
            ),
        }),
      contactType: yup.object(),
      userIds: yup.array().nullable(),
      externalInterviewerIds: yup.array().nullable(),
      companyManagerIds: yup.array().nullable(),
      note: yup.string().nullable().trim(),
      title: yup.string().nullable().trim(),
      startTime: yup
        .string()
        .trim()
        .required(t('modals.SetOnboardingModal.startTimeRequired'))
        .test(
          'greater-than-now',
          t('modals.ScheduleInterviewModal.startTimeGreaterThanCurrentTime'),
          startTimeNotInPastIfToday('interviewDate')
        ),
      endTime: yup
        .string()
        .trim()
        .required(t('modals.ScheduleInterviewModal.endTimeRequired'))
        .test(
          'less-than-start',
          t('modals.ScheduleInterviewModal.endTimeGreaterThanStartTime'),
          (value, ctx) => {
            if (!ctx.parent.startTime) {
              return true;
            }
            const endTime = parseInt(value?.replace(':', ''), 10);
            const startTime = parseInt(
              ctx.parent.startTime?.replace(':', ''),
              10
            );
            return !(endTime <= startTime);
          }
        ),
    });
  }, [t]);

  const slotPrefill = useMemo(() => {
    if (!editableInterview?.slotDates?.length || !me?.userProfile?.timezone) {
      return null;
    }

    const sorted = [...editableInterview.slotDates].sort(
      (a, b) => new Date(a.startDateTime) - new Date(b.startDateTime)
    );
    const first = sorted[0];

    const tzDate = getTimezoneDate(
      first.startDateTime,
      me.userProfile.timezone
    )[0];
    const hours = tzDate.getHours().toString().padStart(2, '0');
    const minutes = tzDate.getMinutes().toString().padStart(2, '0');

    return {
      date: tzDate,
      startTime: `${hours}:${minutes}`,
    };
  }, [editableInterview?.slotDates, me?.userProfile?.timezone]);

  const formik = useFormik({
    initialValues: {
      interviewDate:
        (isEdit || isViewDetails) && editableInterview.status !== 'Requested'
          ? getTimezoneDate(
              editableInterview?.interviewDate,
              me?.userProfile?.timezone
            )[0]
          : slotPrefill?.date || '',
      type:
        isEdit || isViewDetails
          ? {
              value: editableInterview?.type,
              label: editableInterview?.type.replace(
                /([a-z])([A-Z])/g,
                '$1-$2'
              ),
            }
          : { value: 'Video', label: t('modals.ScheduleInterviewModal.video') },
      level:
        isEdit || isViewDetails
          ? {
              value: editableInterview?.level,
              label: editableInterview?.level,
            }
          : '',
      customLocation:
        isEdit || isViewDetails ? editableInterview?.customLocation : '',
      title:
        (isEdit || isViewDetails) && editableInterview.status !== 'Requested'
          ? editableInterview?.title
          : `Interview with ${candidate?.user?.userProfile?.firstName} ${candidate?.user?.userProfile?.lastName} | ${candidate?.jobPost?.jobTitle} (${candidate?.jobPost?.id})`,
      connectionDetail:
        isEdit || isViewDetails ? editableInterview?.connectionDetail : '',
      notificationType:
        isEdit || isViewDetails
          ? {
              value: editableInterview?.notificationType,
              label: editableInterview?.notificationType,
            }
          : '',
      notificationMethod:
        isEdit || isViewDetails
          ? {
              value: editableInterview?.notificationMethod,
              label: editableInterview?.notificationMethod,
            }
          : '',
      contactType:
        isEdit || isViewDetails
          ? {
              value: editableInterview?.contactType,
              label: t(
                `common.InterviewContactTypeOptions.${editableInterview?.contactType}`
              ),
            }
          : {
              value: initialContactType,
              label: t(
                `common.InterviewContactTypeOptions.${initialContactType}`
              ),
            },
      notificationValue:
        isEdit || isViewDetails
          ? {
              value: editableInterview?.notificationValue,
              label: editableInterview?.notificationValue,
            }
          : '',
      userIds:
        isEdit || isViewDetails
          ? editableInterview?.users?.map((user) => ({
              label: user.userName,
              value: user.id,
              originalData: user,
            }))
          : job?.jobPostInterviewers?.length
          ? job?.jobPostInterviewers?.map((user) => ({
              label: user.user.userName,
              value: user.user.id,
              originalData: user,
            }))
          : '',
      externalInterviewerIds:
        isEdit || isViewDetails
          ? editableInterview?.externalInterviewers?.map((user) => ({
              label: `${user.firstName} ${user.lastName} ${
                user.designation ? `(${user.designation})` : ''
              }`,
              value: user.id,
              originalData: user,
            }))
          : '',
      companyManagerIds:
        isEdit || isViewDetails
          ? editableInterview?.companyManagers?.map((user) => ({
              label: `${user.firstName} ${user.lastName} ${
                user.designation ? `(${user.designation})` : ''
              }`,
              value: user.id,
              originalData: user,
            }))
          : '',
      note: isEdit || isViewDetails ? editableInterview?.note : '',
      startTime:
        (isEdit || isViewDetails) && editableInterview.status !== 'Requested'
          ? getTimezoneTime(
              editableInterview?.startTime,
              me?.userProfile?.timezone
            )
          : slotPrefill?.startTime || '',
      endTime:
        (isEdit || isViewDetails) && editableInterview.status !== 'Requested'
          ? getTimezoneTime(
              editableInterview?.endTime,
              me?.userProfile?.timezone
            )
          : '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        console.log('Form submitted with values:', values);
        setIsFetching(true);

        /* if (isEdit) {
          await AdminService.deleteJobApplicationInterview({
            jobApplicationId: candidate.id,
            interviewid: editableInterview?.id,
          });
        } */

        const requestFunction = isEdit
          ? AdminService.changeJobApplicationInterview
          : AdminService.createJobApplicationInterview;

        const payload = {
          jobApplicationId: candidate.id,
          interviewId: editableInterview?.id,
          interviewDate: setTimezoneDate(
            values.interviewDate,
            values.startTime,
            me?.userProfile?.timezone
          ),
          title: values.title,
          contactType: values.contactType?.value,
          type: values.type.value,
          level: values.level?.value,
          customLocation: values.customLocation,
          connectionDetail: values.connectionDetail?.trim(),
          notificationType: values.notificationType?.value,
          notificationMethod: values.notificationMethod?.value,
          notificationValue: values.notificationValue?.value,
          userIds: values?.userIds?.length
            ? values?.userIds?.map((user) => user?.value)
            : [],
          externalInterviewerIds: values?.externalInterviewerIds?.length
            ? values?.externalInterviewerIds?.map((user) => user?.value)
            : [],
          companyManagerIds: values?.companyManagerIds?.length
            ? values?.companyManagerIds?.map((user) => user?.value)
            : [],
          note: values.note,
          startTime: setTimezoneTime(
            values.startTime,
            me?.userProfile.timezone
          ),
          endTime: setTimezoneTime(values.endTime, me?.userProfile.timezone),
          status: isEdit ? 'Rescheduled' : 'Scheduled',
        };

        console.log('Submitting payload:', payload);
        await requestFunction(payload);

        handleClose();
        refetch();
        closeAndNotify(
          t(
            isEdit
              ? 'modals.ScheduleInterviewModal.interviewUpdated'
              : 'modals.ScheduleInterviewModal.interviewCreated'
          )
        );
      } catch (error) {
        if (error.response?.data?.message === 'Duplicity') {
          showModal({
            title: t('modals.ScheduleInterviewModal.error'),
            text: t('modals.ScheduleInterviewModal.duplicityTitle'),
          });
        } else if (error.response?.data?.message === 'ExternalDuplicity') {
          showModal({
            title: t('modals.ScheduleInterviewModal.error'),
            text: t('modals.ScheduleInterviewModal.externalDuplicityTitle'),
          });
        } else {
          showModal({
            title: t('modals.ScheduleInterviewModal.error'),
            text:
              error?.response?.data?.message ||
              error?.message ||
              t('modals.ScheduleInterviewModal.errorMessage'),
          });
        }
      } finally {
        setIsFetching(false);
      }
    },
    enableReinitialize: true,
  });

  // Compute contact type current value and dynamic interview type options
  const contactTypeValue = useMemo(() => {
    const raw = formik?.values?.contactType;
    return raw?.value || raw || initialContactType;
  }, [formik?.values?.contactType, initialContactType]);

  const interviewTypeOptions = useMemo(() => {
    const all = [
      { value: 'InPerson', label: t('modals.ScheduleInterviewModal.inPerson') },
      { value: 'Video', label: t('modals.ScheduleInterviewModal.video') },
      { value: 'Audio', label: t('modals.ScheduleInterviewModal.audio') },
    ];
    return contactTypeValue === 'External'
      ? all.filter((o) => o.value !== 'InPerson')
      : all;
  }, [t, contactTypeValue]);

  useEffect(() => {
    // Ensure current type remains valid when contact type changes
    const current = formik.values.type;
    const valid =
      current && interviewTypeOptions.some((o) => o.value === current.value);
    if (!valid) {
      formik.setFieldValue('type', interviewTypeOptions[0] || '');
    }
    // Include formik in deps to silence linter; setFieldValue is stable
  }, [interviewTypeOptions, formik]);

  // const deleteInterview = async () => {
  //   try {
  //     setIsFetching(true);
  //     await AdminService.cancelJobApplicationInterview({
  //       jobApplicationId: candidate.id,
  //       interviewId: editableInterview?.id,
  //     });
  //     closeAndNotify(t('modals.ScheduleInterviewModal.interviewDeleted'));
  //   } catch (error) {
  //     console.log(error);
  //   } finally {
  //     setIsFetching(false);
  //   }
  // };

  const cancelInterview = async () => {
    try {
      setIsFetching(true);
      await AdminService.cancelJobApplicationInterviewNew({
        jobApplicationId: candidate.id,
        interviewId: editableInterview?.id,
      });
      closeAndNotify(t('modals.ScheduleInterviewModal.interviewCanceled'));
    } catch (error) {
      console.log(error);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    return () => {
      formik.resetForm();
      setIsTriedToSubmit(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  let isInterviewerInvalid = false;
  if (
    formik.values.contactType?.value === 'Client' &&
    (!formik.values.companyManagerIds ||
      formik.values.companyManagerIds?.length === 0)
  ) {
    isInterviewerInvalid = true;
  } else if (
    formik.values.contactType?.value === 'External' &&
    (!formik.values.externalInterviewerIds ||
      formik.values.externalInterviewerIds?.length === 0)
  ) {
    isInterviewerInvalid = true;
  }

  let isConnectionDetailsInvalid = false;

  if (
    formik.values.type.value === 'InPerson' &&
    (!formik.values.customLocation ||
      formik.values.customLocation?.length === 0)
  ) {
    isConnectionDetailsInvalid = true;
  } else if (
    formik.values.type.value !== 'InPerson' &&
    (!formik.values.connectionDetail ||
      formik.values.connectionDetail?.length === 0 ||
      formik.errors.connectionDetail)
  ) {
    isConnectionDetailsInvalid = true;
  }

  const isDisabled =
    !formik.values.interviewDate ||
    !formik.values.startTime ||
    !formik.values.endTime ||
    !formik.values.type ||
    isInterviewerInvalid ||
    isConnectionDetailsInvalid;

  const status = editableInterview?.status;

  const suggestedInterviewDate = getEventDate(
    getTimezoneDate(
      editableInterview?.suggestedInterviewDate,
      me?.userProfile?.timezone,
      editableInterview?.suggestedStartTime
    )[0]
  );
  const suggestedStartTime = convertToAmPm(
    getTimezoneTime(
      editableInterview?.suggestedStartTime,
      me?.userProfile?.timezone
    )
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      enforceFocus={false}
      restoreFocus={false}
      className={classes.ScheduleInterviewModal}
      backdropClassName={classes.backdrop}
      contentClassName={classes.modalContent}
      dialogClassName={classes.dialog}
    >
      <div className={classes.content}>
        <header>
          <h1>
            {isViewDetails
              ? t('modals.ScheduleInterviewModal.viewInterview')
              : status === 'Requested'
              ? t('modals.ScheduleInterviewModal.scheduleRequestedInterview')
              : isEdit
              ? t('modals.ScheduleInterviewModal.editInterview')
              : t('modals.ScheduleInterviewModal.scheduleInterview')}
          </h1>
          <i className={classes.closeIcon} onClick={handleClose}>
            Close
          </i>
        </header>
        <div className={classes.container}>
          <div className={classes.row}>
            <TextInput
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              setFieldValue={formik.setFieldValue}
              error={formik.errors.title}
              setFieldTouched={formik.setFieldTouched}
              label={t('modals.ScheduleInterviewModal.title')}
              placeholder={t('modals.ScheduleInterviewModal.title')}
              readonly={isViewDetails}
              width="100%"
              height={45}
              note={t('common.optional')}
            />
          </div>

          <div className={classes.row}>
            <div className={classes.col}>
              <div className={classes.applicationInfo}>
                <h3>{t('modals.ScheduleInterviewModal.candidate')}</h3>
                <h2>
                  {candidate.user?.userProfile?.firstName}{' '}
                  {candidate.user?.userProfile?.lastName}
                </h2>
                {(status === 'Accepted' ||
                  status === 'Declined' ||
                  status === 'ChangeRequest') && (
                  <div className={classes.status}>
                    <div
                      className={classNames(classes.statusText, {
                        [classes.statusDeclined]: status === 'Declined',
                        [classes.changeRequest]: status === 'ChangeRequest',
                      })}
                    >
                      {status.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                    {status === 'ChangeRequest' && (
                      <span className={classes.statusDate}>
                        {`${t('modals.ScheduleInterviewModal.requested')}:
                                  ${suggestedInterviewDate} ${suggestedStartTime}`}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={classNames(classes.col, classes.positionCol)}>
              <div
                className={classNames(
                  classes.applicationInfo,
                  classes.position
                )}
              >
                <h3>{t('modals.ScheduleInterviewModal.position')}</h3>
                <h2>{job?.jobTitle}</h2>
                <span className={classes.jobId}>
                  {candidate?.jobPost?.company?.name}
                  {' | '}
                  {t('modals.ScheduleInterviewModal.jobId')}:{' '}
                  {candidate?.jobPostId}
                </span>
              </div>
            </div>
          </div>
          {(editableInterview?.slotDates?.length > 0 ||
            editableInterview?.status === 'Requested') && (
            <div className={classes.row}>
              <div className={classes.slots}>
                <h4 className={classes.slotsTitle}>
                  {t('modals.ScheduleInterviewModal.suggestedSlots')}
                </h4>
                <div className={classes.slotsList}>
                  {editableInterview?.slotDates &&
                  editableInterview?.slotDates?.length > 0 ? (
                    [...editableInterview.slotDates]
                      .sort(
                        (a, b) =>
                          new Date(a.startDateTime) - new Date(b.startDateTime)
                      )
                      .map((slot) => {
                        const tzDate = getTimezoneDate(
                          slot.startDateTime,
                          me?.userProfile?.timezone
                        )[0];
                        const slotDate = getEventDate(tzDate);
                        const h = tzDate.getHours().toString().padStart(2, '0');
                        const m = tzDate
                          .getMinutes()
                          .toString()
                          .padStart(2, '0');
                        const slotTime = convertToAmPm(`${h}:${m}`);

                        return (
                          <div
                            key={slot.startDateTime}
                            className={classes.slotItem}
                          >
                            <div className={classes.slotDate}>{slotDate}</div>
                            <div className={classes.slotTime}>{slotTime}</div>
                          </div>
                        );
                      })
                  ) : (
                    <div className={classes.noSlots}>
                      {t('modals.ScheduleInterviewModal.noSlotsAvailable')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className={classes.row}>
            <div className={classes.col}>
              <SegmentedControl
                readOnly={isViewDetails}
                ariaLabel={t('modals.ScheduleInterviewModal.segmentAriaLabel')}
                items={contactTypeOptions}
                selectedItem={formik.values.contactType}
                onChange={(val) => {
                  formik.setFieldValue('contactType', val);
                  // Clear dependent, contact-type-specific selections
                  formik.setFieldValue('userIds', []);
                  formik.setFieldValue('companyManagerIds', []);
                  formik.setFieldValue('externalInterviewerIds', []);
                }}
              />
            </div>
          </div>
          <div className={classes.row}>
            <div className={classes.col}>
              <DatePicker
                dateFieldWidth={350}
                label={t('modals.ScheduleInterviewModal.interviewDate')}
                name="interviewDate"
                readonly={isViewDetails}
                value={formik.values.interviewDate}
                setFieldValue={formik.setFieldValue}
                error={formik.errors.interviewDate}
                // touched={isDelegatesTriedToSubmit}
                setFieldTouched={formik.setFieldTouched}
              />
            </div>
            <div className={classes.col}>
              <TimeRange
                fromValue={formik.values.startTime}
                toValue={formik.values.endTime}
                setFieldValue={formik.setFieldValue}
                fromName="startTime"
                toName="endTime"
                readOnly={isViewDetails}
                error={formik.errors.endTime}
                startTimeError={formik.errors.startTime}
                touched={isTriedToSubmit}
                showDuration
                label={t('modals.ScheduleInterviewModal.interviewTime')}
              />
            </div>
          </div>
          <div className={classes.row}>
            <div className={classNames(classes.col, classes.small)}>
              <FormDropdown
                name="type"
                label={t('modals.ScheduleInterviewModal.interviewMode')}
                placeholder={t(
                  'modals.ScheduleInterviewModal.selectInterviewMode'
                )}
                width={170}
                height={45}
                readOnly={isViewDetails}
                options={interviewTypeOptions}
                value={formik.values.type}
                setFieldValue={formik.setFieldValue}
              />
            </div>
            <div className={classes.col}>
              {formik.values.type?.value === 'InPerson' && (
                <TextInput
                  readonly={isViewDetails}
                  name="customLocation"
                  value={formik.values.customLocation}
                  onChange={formik.handleChange}
                  width="100%"
                  height={45}
                  label={t('modals.ScheduleInterviewModal.Address')}
                  placeholder={t(
                    'modals.ScheduleInterviewModal.AddressPlaceholder'
                  )}
                />
              )}
              {formik.values.type?.value === 'Video' && (
                <TextInput
                  readonly={isViewDetails}
                  name="connectionDetail"
                  value={formik.values.connectionDetail}
                  onChange={formik.handleChange}
                  width="100%"
                  height={45}
                  label={t('modals.ScheduleInterviewModal.meetingLink')}
                  placeholder={t(
                    'modals.ScheduleInterviewModal.meetingLinkPlaceholder'
                  )}
                />
              )}
              {formik.values.type?.value === 'Audio' && (
                <TextInput
                  readonly={isViewDetails}
                  name="connectionDetail"
                  value={formik.values.connectionDetail}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.errors.connectionDetail}
                  touched={formik.touched.connectionDetail || isTriedToSubmit}
                  staticErrorPositon
                  width="100%"
                  height={45}
                  label={t('modals.ScheduleInterviewModal.phone')}
                  placeholder={t(
                    'modals.ScheduleInterviewModal.phonePlaceholder'
                  )}
                />
              )}
            </div>
          </div>
          <div className={classes.row} style={{ display: 'none' }}>
            <div className={classes.col}>
              <div className={classes.dropdowns}>
                <div className={classes.dropdownContainer}>
                  <h4 className={classes.dropdownTitle}>
                    {t('modals.ScheduleInterviewModal.interviewMode')}
                  </h4>
                  <Dropdown
                    readOnly={isViewDetails}
                    placeholder={t(
                      'modals.ScheduleInterviewModal.selectInterviewMode'
                    )}
                    width={170}
                    options={interviewTypeOptions}
                    activeOption={formik.values.type}
                    setActiveOption={(option) =>
                      formik.setFieldValue('type', option)
                    }
                  />
                </div>
                {/* <div className={classes.dropdownContainer}>
                  <h4 className={classes.dropdownTitle}>
                    {t('modals.ScheduleInterviewModal.interviewLevel')}
                  </h4>
                  <Dropdown
                    width={170}
                    options={[
                      {
                        value: 'Level1',
                        label: t('modals.ScheduleInterviewModal.level1'),
                      },
                      {
                        value: 'Level2',
                        label: t('modals.ScheduleInterviewModal.level2'),
                      },
                      {
                        value: 'Level3',
                        label: t('modals.ScheduleInterviewModal.level3'),
                      },
                    ]}
                    activeOption={formik.values.level}
                    setActiveOption={(option) =>
                      formik.setFieldValue('level', option)
                    }
                  />
                </div> */}
              </div>
            </div>
            <div className={classes.col} style={{ visibility: 'hidden' }}>
              <div className={classes.dropdownsCol}>
                <div className={classes.dropdownTitle}>
                  {t('modals.ScheduleInterviewModal.addNotification')}
                  <span className={classes.note}>
                    ({t('modals.ScheduleInterviewModal.optional')})
                  </span>
                </div>
                <div className={classes.dropdowns}>
                  <Dropdown
                    readOnly={isViewDetails}
                    width={139}
                    options={[
                      {
                        value: 'Notification',
                        label: t('modals.ScheduleInterviewModal.notification'),
                      },
                      {
                        value: 'Email',
                        label: t('modals.ScheduleInterviewModal.email'),
                      },
                      {
                        value: 'None',
                        label: t('modals.ScheduleInterviewModal.none'),
                      },
                    ]}
                    activeOption={formik.values.notificationMethod}
                    setActiveOption={(option) =>
                      formik.setFieldValue('notificationMethod', option)
                    }
                  />
                  <Dropdown
                    readOnly={isViewDetails}
                    width={77}
                    options={
                      formik.values.notificationType.value === 'Minutes'
                        ? [
                            { value: '30', label: '30' },
                            { value: '45', label: '45' },
                            { value: '60', label: '60' },
                          ]
                        : [
                            { value: '1', label: '1' },
                            { value: '2', label: '2' },
                            { value: '3', label: '3' },
                            { value: '4', label: '4' },
                          ]
                    }
                    activeOption={formik.values.notificationValue}
                    setActiveOption={(option) =>
                      formik.setFieldValue('notificationValue', option)
                    }
                  />
                  <Dropdown
                    readOnly={isViewDetails}
                    width={116}
                    options={[
                      {
                        value: 'Minutes',
                        label: t('modals.ScheduleInterviewModal.minutes'),
                      },
                      {
                        value: 'Hours',
                        label: t('modals.ScheduleInterviewModal.hours'),
                      },
                    ]}
                    activeOption={formik.values.notificationType}
                    setActiveOption={(option) =>
                      formik.setFieldValue('notificationType', option)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={classes.row}>
            <div className={classes.col}>
              {contactTypeValue === 'Internal' && (
                <div className={classes.usersContainer}>
                  <div className={classes.selectContainer}>
                    <MultiDropdown
                      readOnly={isViewDetails}
                      maxLength={5}
                      note={t('modals.ScheduleInterviewModal.selectUpTo5')}
                      width={350}
                      label={t('modals.ScheduleInterviewModal.interviewers')}
                      fetchOptions={{
                        dataName: 'userOptionPostJobSuccess',
                        getDataHandler: AdminService.getUsers,
                        labelName: 'userName',
                        isUserList: true,
                      }}
                      value={formik.values.userIds}
                      name="userIds"
                      setFieldValue={formik.setFieldValue}
                      placeholder={t(
                        'modals.ScheduleInterviewModal.interviewersPlaceholder'
                      )}
                    />
                  </div>
                  <div className={classes.selectedUsers}>
                    <SelectedUsers
                      invitedUsers={formik.values.userIds}
                      withFormik
                      readOnly={isViewDetails}
                      setInvitedUsers={(users) =>
                        formik.setFieldValue('userIds', users)
                      }
                    />
                  </div>
                </div>
              )}
              {contactTypeValue === 'Client' && (
                <div className={classes.usersContainer}>
                  <div className={classes.selectContainer}>
                    <MultiDropdown
                      maxLength={5}
                      readOnly={isViewDetails}
                      note={t('modals.ScheduleInterviewModal.selectUpTo5')}
                      width={350}
                      label={t('modals.ScheduleInterviewModal.companyContacts')}
                      fetchOptions={{
                        dataName: 'companyOptionPostJobSuccess',
                        getDataHandler: AdminService.getAllManagers,
                        fetchHanlderOptions: {
                          companyId: job?.company?.id,
                        },
                        isExternalUserList: true,
                      }}
                      value={formik.values.companyManagerIds}
                      name="companyManagerIds"
                      setFieldValue={formik.setFieldValue}
                      placeholder={t(
                        'modals.ScheduleInterviewModal.interviewersPlaceholder'
                      )}
                    />
                  </div>
                  <div className={classes.selectedUsers}>
                    <SelectedUsers
                      readOnly={isViewDetails}
                      invitedUsers={formik.values.companyManagerIds}
                      withFormik
                      isExternalUserList
                      setInvitedUsers={(users) =>
                        formik.setFieldValue('companyManagerIds', users)
                      }
                    />
                  </div>
                </div>
              )}
              {contactTypeValue === 'External' && (
                <div className={classes.usersContainer}>
                  <div className={classes.selectContainer}>
                    <MultiDropdown
                      readOnly={isViewDetails}
                      maxLength={5}
                      width={350}
                      note={t('modals.ScheduleInterviewModal.selectUpTo5')}
                      label={t(
                        'modals.ScheduleInterviewModal.externalInterviewers'
                      )}
                      fetchOptions={{
                        dataName: 'externalOptionPostJobSuccess',
                        getDataHandler: AdminService.getExternalInterviewers,
                        isExternalUserList: true,
                      }}
                      value={formik.values.externalInterviewerIds}
                      name="externalInterviewerIds"
                      setFieldValue={formik.setFieldValue}
                      placeholder={t(
                        'modals.ScheduleInterviewModal.interviewersPlaceholder'
                      )}
                    />
                  </div>
                  <div className={classes.selectedUsers}>
                    <SelectedUsers
                      invitedUsers={formik.values.externalInterviewerIds}
                      withFormik
                      readOnly={isViewDetails}
                      isExternalUserList
                      setInvitedUsers={(users) =>
                        formik.setFieldValue('externalInterviewerIds', users)
                      }
                    />
                  </div>
                </div>
              )}
            </div>
            <div className={classes.col}>
              <Textarea
                readonly={isViewDetails}
                name="note"
                width="100%"
                onChange={formik.handleChange}
                value={formik.values.note}
                label={t('modals.ScheduleInterviewModal.emailMessage')}
                note={t('modals.ScheduleInterviewModal.optional')}
                height={159}
                placeholder={t('modals.ScheduleInterviewModal.enterNotes')}
                // maxLength={5000}
              />
            </div>
          </div>
        </div>
        {!isViewDetails && (
          <footer>
            <FormButton
              disabled={isEdit ? formik.isSubmitting : !formik.dirty}
              variant={isEdit ? 'delete' : 'reset'}
              style={{
                width: 250,
                height: 52,
              }}
              onClick={isEdit ? cancelInterview : formik.resetForm}
            >
              {isEdit
                ? t('modals.ScheduleInterviewModal.cancel')
                : t('modals.ScheduleInterviewModal.clear')}
            </FormButton>
            <FormButton
              style={{
                width: 250,
                height: 52,
              }}
              variant="submit"
              disabled={isDisabled || !formik.dirty || formik.isSubmitting}
              onClick={() => {
                console.log('Submit button clicked', {
                  isDisabled,
                  dirty: formik.dirty,
                  isSubmitting: formik.isSubmitting,
                  values: formik.values,
                  errors: formik.errors,
                });
                setIsTriedToSubmit(true);
                formik.handleSubmit();
              }}
            >
              {isEdit
                ? t('modals.ScheduleInterviewModal.update')
                : t('modals.ScheduleInterviewModal.schedule')}
            </FormButton>
          </footer>
        )}
      </div>
    </Modal>
  );
}
